import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getAuthenticatedUserContacts } from 'store/AuthenticatedUserContact/authenticatedUserContactThunk';
import { IMPERSONATING_USER } from '../../../models/constants';
import { getSessionItem, setSessionItem, removeSessionItem } from '../../../core/services/sessionStorageService';
import { reloadPage } from '../../../core/services/redirectService';

export type NavigateAsUser = {
    name: string;
    company?: string;
    icId?: string;
};

type UseNavigateAs = {
    showNavigateAs: Nullable<boolean>;
    navigateAsUser: Nullable<NavigateAsUser>;
    navigateAsUserContacts: Nullable<NavigateAsUser[]>;
    changeNavigateAsUserCallback: (event: Event) => void;
    stopNavigateAsCallback: (event: Event) => void;
};

export const useNavigateAs = (): UseNavigateAs => {
    const dispatch = useAppDispatch();
    let navigateAsUser: Nullable<NavigateAsUser> = null;
    let showNavigateAs: Nullable<boolean> = null;
    const userContacts = useAppSelector(state => state.authenticatedUserContacts.userContacts);
    const authenticatedUser = useAppSelector(state => state.applicationContext.context?.user);

    const navigateAsUserContacts = userContacts ? userContacts.map(c => {
        const user: NavigateAsUser = {
            name: `${c.lastName} ${c.firstName}`,
            company: c.company ? c.company : '',
            icId: c.icId ? c.icId : '',
        };

        return user;
    }) : [];

    const hasContacts = navigateAsUserContacts && navigateAsUserContacts.length > 0;
    if (hasContacts) {
        navigateAsUser = getSessionItem<NavigateAsUser>(IMPERSONATING_USER);
        showNavigateAs = true;
    }

    useEffect(() => {
        if (authenticatedUser?.isInternal) {
            dispatch(getAuthenticatedUserContacts());
        }
    }, [dispatch, authenticatedUser]);

    const changeNavigateAsUserCallback = useCallback(
        (evt: Event) => {
            const user = (evt as any).detail.user as NavigateAsUser;
            const impersonatingUser = getSessionItem<NavigateAsUser>(IMPERSONATING_USER);
            if (!impersonatingUser || user.icId !== impersonatingUser.icId) {
                setSessionItem<NavigateAsUser>(IMPERSONATING_USER, user);
                reloadPage();
            }
        }, []);

    const stopNavigateAsCallback = useCallback(
        () => {
            removeSessionItem(IMPERSONATING_USER);
            reloadPage();
        }, []);

    return { showNavigateAs, navigateAsUser, navigateAsUserContacts, changeNavigateAsUserCallback, stopNavigateAsCallback };
};
